import { Direction } from '@angular/cdk/bidi';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { switchMap } from 'rxjs';
import {
  IconColumn,
  TableConfig,
  TextColumn,
} from 'src/app/@forms/components/mot-table/mot-table-config.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import {
  AFTER_MIDNIGHT,
  ALT_TABLE_EMPTY,
  ARRIVAL_DESTINATION_STATION,
  ARRIVAL_TIME,
  CROWDEDNESS_HEAVY,
  CROWDEDNESS_LIGHT,
  CROWDEDNESS_MEDIUM,
  CROWDEDNESS_UNKNOWN,
  DAY,
  DAYS_NAMES,
  DEPARTURE_TIME,
  EXIT_ORIGIN_STATION,
  LINE_CROWDEDNESS,
  LINE_NOT_ACTIVE_ON_SELECTED_DAY,
  LINE_WILL_RETURN_ON_DAY,
  PICK_DAY,
  STOPS_CHANGED_FOR_REQUESTED_DATE,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eCrowdednessType } from 'src/app/modules/route-planner/enums/route-planning/crowdedness-type.enum';
import { NextDayOfService } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/next-day-of-service.interface';

export interface TripCalendarItem {
  arrivalTime: string;
  departureTime: string;
  crowdedness?: eCrowdednessType;
  isNextDay?: boolean;
}

@Component({
    selector: 'app-search-line-daily-calendar',
    templateUrl: './search-line-daily-calendar.component.html',
    styleUrls: ['./search-line-daily-calendar.component.scss'],
    standalone: false
})
export class SearchLineDailyCalendarComponent implements OnInit, OnChanges {
  @Output() onDayChange = new EventEmitter<Date>();

  @Input() dailyCalendar: TripCalendarItem[] = [];
  @Input() isLoadingCalendarData: boolean;
  @Input() nextDayOfService: NextDayOfService;
  @Input() stopsChanged: boolean = false;

  pickDayText: string
  dayText: string
  lineNotActiveOnSelectedDayText: string
  lineWillReturnOnDayText: string
  exitOriginStationText: string
  arrivalDestinationStationText: string
  lineCrowdednessText: string
  afterMidnightText: string
  emptyTableAltText: string
  stopsChangedText: string
  dayFormControl = new FormControl<Date>(new Date());
  minDate: Date = new Date();
  maxDate: Date = new Date(new Date().setDate(new Date().getDate() + 6));
  config: TableConfig<TripCalendarItem> = null

  /** Index of the calendar row closest to the current time, scrolled into view once on the initial load. */
  closestIndex: number | null = null;
  private hasScrolledToClosest = false;

  direction: Direction

  constructor(public readonly utilsService: UtilsService, public readonly languageService: LanguageService) {}

  ngOnInit(): void {
    this.dayFormControl.valueChanges.subscribe((val) => {
      if (!!val) this.onDayChange.emit(val);
    });

    this.initializeMultiLanguageTexts()


  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.hasScrolledToClosest &&
      changes['dailyCalendar'] &&
      this.dailyCalendar?.length
    ) {
      this.hasScrolledToClosest = true;
      this.closestIndex = this.findClosestToNowIndex(this.dailyCalendar);
    }
  }

  private findClosestToNowIndex(items: TripCalendarItem[]): number | null {
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    let closestIndex: number | null = null;
    let smallestDiff = Infinity;

    items.forEach((item, index) => {
      const minutes = this.timeStringToMinutes(item.arrivalTime, item.isNextDay);
      if (minutes === null) {
        return;
      }
      const diff = Math.abs(minutes - nowMinutes);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  private timeStringToMinutes(time: string, isNextDay?: boolean): number | null {
    const match = time?.match(/^(\d{1,2}):(\d{2})/);
    if (!match) {
      return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return hours * 60 + minutes + (isNextDay ? 24 * 60 : 0);
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.pickDayText = PICK_DAY[language.language]
      this.dayText = DAY[language.language]
      this.lineNotActiveOnSelectedDayText = LINE_NOT_ACTIVE_ON_SELECTED_DAY[language.language]
      this.lineWillReturnOnDayText = LINE_WILL_RETURN_ON_DAY[language.language]
      this.exitOriginStationText = EXIT_ORIGIN_STATION[language.language]
      this.arrivalDestinationStationText = ARRIVAL_DESTINATION_STATION[language.language]
      this.lineCrowdednessText = LINE_CROWDEDNESS[language.language]
      this.afterMidnightText = AFTER_MIDNIGHT[language.language]
      this.emptyTableAltText = ALT_TABLE_EMPTY[language.language]
      this.stopsChangedText = STOPS_CHANGED_FOR_REQUESTED_DATE[language.language]


      this.config = {
        Columns: [
          {
            columnType: new TextColumn<TripCalendarItem>(
              (item) => item.isNextDay ? { text: this.direction === 'ltr' ? '(+1)' : '(1+)', cssClass: 'text-red-500' } : null
            ),
            columnId: 'arrivalTime',
            header: this.exitOriginStationText,
          },
          {
            columnType: new TextColumn(),
            columnId: 'departureTime',
            header: this.arrivalDestinationStationText,
          },
          {
            columnType: new IconColumn<TripCalendarItem>(
              (item) => item.crowdedness !== undefined && item.crowdedness !== null,
              (item) => this.getImageFromCrowdedness(item.crowdedness),
              (item) =>
                this.getCrowdednessAlt(item?.crowdedness, language.language)
            ),
            cellClass: '!text-center',
            columnId: 'lineCrowdedness',
            header: this.lineCrowdednessText,
            headerClass: '!text-center',
          },
        ],
      }
    })
  }

  private getImageFromCrowdedness(
    crowdedness?: eCrowdednessType
  ): string | null {
    switch (crowdedness) {
      case eCrowdednessType.LIGHT:
        return 'crowdedness-light';
      case eCrowdednessType.MEDIUM:
        return 'crowdedness-medium';
      case eCrowdednessType.HEAVY:
        return 'crowdedness-heavy';
      default:
        return 'crowdedness-empty';
    }
  }

  private getCrowdednessAlt(
    crowdedness: eCrowdednessType | undefined,
    language: string
  ): string {
    switch (crowdedness) {
      case eCrowdednessType.LIGHT:
        return CROWDEDNESS_LIGHT[language];
      case eCrowdednessType.MEDIUM:
        return CROWDEDNESS_MEDIUM[language];
      case eCrowdednessType.HEAVY:
        return CROWDEDNESS_HEAVY[language];
      default:
        return CROWDEDNESS_UNKNOWN[language];
    }
  }

  getNextDayOfServiceText() {
    if(this.nextDayOfService) {
      return `${this.lineWillReturnOnDayText} ${DAYS_NAMES[this.languageService.getSelectedLanguage().language][this.nextDayOfService.dayOfService]} - ${this.nextDayOfService.dateOfServiceString}`
    }
    return ""
  }
}
